import { Spacing } from '@persona-id/design-tokens';
import { GridIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import { compact } from 'lodash/fp';
import { ChangeEvent, FC, FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import NavBack from 'dashboard/components/NavBack';
import Tooltip from 'dashboard/components/Tooltip';
import WorkflowStatusSticker from 'dashboard/components/WorkflowStatusSticker';
import { StickerContainer } from 'dashboard/components/WorkflowStatusSticker/WorkflowStatusSticker';
import WorkflowVersionStatusSticker from 'dashboard/components/WorkflowVersionStatusSticker';
import Form from 'dashboard/layouts/FormLayout/Form';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2/ShowViewLayout';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Environment } from 'dashboard/models/environment';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import { TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowBlockStatus } from 'dashboard/models/workflow-block';
import {
  WorkflowStepInput,
  WorkflowVersionDeploymentStatus,
  WorkflowVersionStatus,
} from 'dashboard/models/workflow-version';
import WorkflowValidationErrorsDropdown from 'dashboard/views/DashboardWorkflowsShowView/WorkflowValidationErrorsDropdown';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { EventType } from 'lib/constants/events';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import usePermission from 'lib/hooks/usePermission';
import { usePreserveQueries } from 'lib/hooks/usePreserveQuery';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { eventTypeFor } from 'lib/workflows';

export enum MoreMenuItemKey {
  Settings = 'Settings',
  Runs = 'Runs',
  VersionHistory = 'VersionHistory',
  PlatformConnections = 'PlatformConnections',
  Deactivate = 'Deactivate',
  Activate = 'Activate',
  CreateRuns = 'CreateRuns',
  Archive = 'Archive',
}

const getMoreMenuItemsForInputStep = ({ trigger }: WorkflowStepInput) => {
  switch (trigger?.type) {
    case ApiModel.TriggerComputedField:
      return [MoreMenuItemKey.Runs, MoreMenuItemKey.VersionHistory];
    case ApiModel.TriggerCase:
      return [
        MoreMenuItemKey.Runs,
        MoreMenuItemKey.VersionHistory,
        MoreMenuItemKey.PlatformConnections,
        MoreMenuItemKey.Archive,
      ];
    case ApiModel.TriggerEvent:
      return Object.values(MoreMenuItemKey);
    default:
      return [
        MoreMenuItemKey.Settings,
        MoreMenuItemKey.Runs,
        MoreMenuItemKey.VersionHistory,
        MoreMenuItemKey.PlatformConnections,
        MoreMenuItemKey.Deactivate,
        MoreMenuItemKey.Activate,
        MoreMenuItemKey.Archive,
      ];
  }
};

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
  height: 22px;
`;

interface WorkflowHeaderProps {
  basePathRoot: string;
  canUndo: boolean;
  currentEnvironmentType: Environment['type'];
  editable: boolean;
  hasStepsConfig: boolean;
  hasWritePermission: boolean;
  inputStep: WorkflowStepInput;
  isCurrentPublishedVersion: boolean;
  hasLatestPublishedVersion: boolean;
  isDirty: boolean;
  isNextVersionInRollout?: boolean;
  isParentActive: boolean;
  isParentActiveInEnvironment: boolean;
  isParentArchived: boolean;
  isParentInactive: boolean;
  isShowingVersionHistory: boolean;
  isVersionActivatable: boolean;
  loading?: boolean;
  moreMenuItemKeys?: MoreMenuItemKey[];
  organization: Organization;
  parentId: string;
  parentName: string;
  rolloutPercentage?: number;
  validationErrors: WorkflowError[];
  versionId: string;
  versionStatus: WorkflowVersionStatus | WorkflowBlockStatus;
  workflowVersionDeploymentStatus?: WorkflowVersionDeploymentStatus;
  closeWorkflowVersionView: () => void;
  onClickError: (index: number) => void;
  onClickSave: () => void;
  onClickDiscard: () => void;
  onUpdateWorkflow: (attributes: { name?: string; description?: string }) => void;
  openActivateWorkflowModal: () => void;
  openArchiveWorkflowModal: () => void;
  openDeactivateWorkflowModal: () => void;
  openManualTriggerModal: () => void;
  openPublishWorkflowModal: () => void;
  showWorkflowVersions: () => void;
  createSimulatedWorkflowRun: () => void;
  undoWorkflow: () => void;
}

const WorkflowHeader: FC<WorkflowHeaderProps> = ({
  basePathRoot,
  canUndo,
  currentEnvironmentType,
  editable,
  hasStepsConfig,
  hasWritePermission,
  inputStep,
  isCurrentPublishedVersion,
  hasLatestPublishedVersion,
  isDirty,
  isNextVersionInRollout,
  isParentActive,
  isParentActiveInEnvironment,
  isParentArchived,
  isParentInactive,
  isShowingVersionHistory,
  isVersionActivatable,
  loading,
  moreMenuItemKeys,
  organization,
  parentId,
  parentName,
  rolloutPercentage,
  validationErrors,
  versionId,
  versionStatus,
  workflowVersionDeploymentStatus,
  closeWorkflowVersionView,
  createSimulatedWorkflowRun,
  onClickError,
  onClickSave,
  onClickDiscard,
  onUpdateWorkflow,
  openActivateWorkflowModal,
  openArchiveWorkflowModal,
  openDeactivateWorkflowModal,
  openManualTriggerModal,
  openPublishWorkflowModal,
  showWorkflowVersions,
  undoWorkflow,
}) => {
  const { trigger } = inputStep;
  const isPersonerd = useIsPersonerd();
  const isPlatformConnectionsEnabled = useFeatureFlag(
    'global:enable-platform-connection-dashboard',
  );
  const preserveQueries = usePreserveQueries();

  const hasRunsViewPermission = usePermission(Permission.WorkflowRunList);
  const hasWorkflowsEnhanced = !productFeatureDisabled(
    organization,
    ProductFeatureName.WorkflowsEnhanced,
    currentEnvironmentType,
  );
  const hasWorkflowsFree = !productFeatureDisabled(
    organization,
    ProductFeatureName.WorkflowsFree,
    currentEnvironmentType,
  );
  const hasWorkflowsBasic = !productFeatureDisabled(
    organization,
    ProductFeatureName.WorkflowsBasic,
    currentEnvironmentType,
  );

  const moreItems: {
    id: string;
    label: string;
    hidden?: boolean;
    routeTo?: string;
    variant?: 'basic' | 'danger';
    onClick?: () => void;
  }[] = useMemo(() => {
    const itemKeys = moreMenuItemKeys || getMoreMenuItemsForInputStep(inputStep);
    return compact(
      itemKeys.map((key) => {
        switch (key) {
          case MoreMenuItemKey.Settings:
            return {
              id: 'workflow-header__dropdown-settings',
              label: 'Settings',
              routeTo: `/dashboard/${basePathRoot}/${parentId}?${preserveQueries({ version: versionId, config: 'settings' })}`,
            };
          case MoreMenuItemKey.Runs:
            if (!hasRunsViewPermission) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-run',
              label: 'Runs',
              routeTo: `/dashboard/${basePathRoot}/${parentId}/runs`,
            };
          case MoreMenuItemKey.VersionHistory:
            return {
              id: 'workflow-header__dropdown-history',
              label: isShowingVersionHistory ? 'Close Version History' : 'Version History',
              onClick: isShowingVersionHistory ? closeWorkflowVersionView : showWorkflowVersions,
            };
          case MoreMenuItemKey.PlatformConnections:
            if (!isPlatformConnectionsEnabled) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-platform-connections',
              label: 'Connections',
              routeTo: `/dashboard/platform-connections/${versionId}`,
            };
          case MoreMenuItemKey.Deactivate:
            if (!hasWritePermission || !isParentActive || isNextVersionInRollout) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-deactivate',
              label: 'Deactivate',
              onClick: openDeactivateWorkflowModal,
            };
          case MoreMenuItemKey.Activate:
            if (!hasWritePermission || !isParentInactive || !isVersionActivatable) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-activate',
              label: 'Activate',
              onClick: openActivateWorkflowModal,
            };
          case MoreMenuItemKey.CreateRuns:
            if (!isPersonerd) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-create-new-runs',
              label: 'Create New Runs',
              onClick: openManualTriggerModal,
            };
          case MoreMenuItemKey.Archive:
            if (!editable || !hasWritePermission || isParentArchived) {
              return;
            }
            return {
              id: 'workflow-header__dropdown-archive',
              label: 'Archive',
              variant: 'danger' as const,
              onClick: openArchiveWorkflowModal,
            };
        }
      }),
    );
  }, [
    basePathRoot,
    parentId,
    closeWorkflowVersionView,
    editable,
    hasRunsViewPermission,
    hasWritePermission,
    isNextVersionInRollout,
    isPersonerd,
    isShowingVersionHistory,
    isVersionActivatable,
    openActivateWorkflowModal,
    openArchiveWorkflowModal,
    openDeactivateWorkflowModal,
    openManualTriggerModal,
    showWorkflowVersions,
    inputStep,
    isParentActive,
    isParentArchived,
    isParentInactive,
    versionId,
    moreMenuItemKeys,
    preserveQueries,
    isPlatformConnectionsEnabled,
  ]);

  const [parentNameState, setParentNameState] = useState(parentName);

  const handleBlurWorkflowName = () => {
    onUpdateWorkflow({ name: parentNameState });
  };

  const handleChangeWorkflowName = (e: ChangeEvent<HTMLInputElement>) => {
    setParentNameState(e.target.value);
  };

  const handleUpdateName = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onUpdateWorkflow({ name: parentNameState });
  };

  const publishButton = useMemo(() => {
    if (hasWorkflowsFree && !hasWorkflowsBasic && !hasWorkflowsEnhanced) {
      return (
        <Tooltip tooltip='You are not able to publish any changes to workflows on your current plan.'>
          <Button
            disabled
            id='workflows-show__publish_button'
            isPending={loading}
            onClick={openPublishWorkflowModal}
          >
            Publish
          </Button>
        </Tooltip>
      );
    } else if (hasWritePermission && isNextVersionInRollout) {
      return (
        <Button
          disabled={isShowingVersionHistory}
          id='workflows-show__publish_button'
          isPending={loading}
          onClick={openPublishWorkflowModal}
        >
          Rollout
        </Button>
      );
    } else {
      return (
        <Button
          disabled={
            !hasWritePermission ||
            loading ||
            !editable ||
            (!isDirty && isCurrentPublishedVersion) ||
            isDirty
          }
          id='workflows-show__publish_button'
          isPending={loading}
          onClick={openPublishWorkflowModal}
        >
          Publish
        </Button>
      );
    }
  }, [
    editable,
    hasWorkflowsBasic,
    hasWorkflowsEnhanced,
    hasWorkflowsFree,
    hasWritePermission,
    isCurrentPublishedVersion,
    isDirty,
    isNextVersionInRollout,
    isShowingVersionHistory,
    loading,
    openPublishWorkflowModal,
  ]);

  const showSimulateButton =
    useFeatureFlag('dashboard:enable-workflows-simulate') &&
    trigger?.type === ApiModel.TriggerEvent;

  const simulateSupportedEvent =
    trigger &&
    [EventType.Inquiry, EventType.Transaction, EventType.Case].includes(
      eventTypeFor(trigger as TriggerEvent),
    );

  const navBackUrl = useMemo<string>(() => {
    if (trigger?.type === ApiModel.TriggerComputedField) {
      return `/dashboard/transaction-types/${trigger.attributes['field-schema-object-token']}`;
    }
    return `/dashboard/${basePathRoot}`;
  }, [trigger, basePathRoot]);

  return (
    <FullScreenHeaderContainer role='banner'>
      <HorizontalContainer>
        <NavBack to={navBackUrl} />
        <Form onSubmit={handleUpdateName}>
          <DynamicInput
            aria-label="Edit workflow's name"
            disabled={trigger?.type === ApiModel.TriggerComputedField}
            value={parentNameState}
            onBlur={handleBlurWorkflowName}
            onChange={handleChangeWorkflowName}
          />
        </Form>
        {trigger == null && (
          <StatusWrapper>
            <StickerContainer>
              <Sticker variant='teal' icon={<GridIcon />} aria-label='This workflow is a module'>
                Module
              </Sticker>
            </StickerContainer>
          </StatusWrapper>
        )}
        <StatusWrapper>
          {isParentActive && !isParentActiveInEnvironment ? (
            <StickerContainer>
              <Sticker variant='caution'>Active in other environment</Sticker>
            </StickerContainer>
          ) : (
            <WorkflowStatusSticker workflowStatus={isParentActiveInEnvironment} />
          )}
        </StatusWrapper>
        <StatusWrapper>
          <WorkflowVersionStatusSticker
            workflowVersionDeploymentStatus={workflowVersionDeploymentStatus}
            versionStatus={versionStatus}
            percentage={rolloutPercentage}
          />
        </StatusWrapper>
      </HorizontalContainer>
      <HStack spacing={Spacing[2]}>
        {validationErrors.length > 0 && (
          <WorkflowValidationErrorsDropdown
            validationErrors={validationErrors}
            onClickError={onClickError}
          />
        )}
        {editable && hasWritePermission && (
          <Button
            disabled={!isDirty || loading || !editable || !canUndo}
            id='workflows-show__undo_button'
            isPending={loading}
            variant='basic'
            onClick={undoWorkflow}
          >
            Undo
          </Button>
        )}
        <Button
          disabled={!isDirty || loading || !editable || !hasWritePermission}
          id='workflows-show__save_button'
          isPending={loading}
          variant='basic'
          onClick={onClickSave}
        >
          Save
        </Button>
        {!isCurrentPublishedVersion && hasLatestPublishedVersion && editable && (
          <Button
            id='workflows-show__discard_button'
            isPending={loading}
            variant='basic'
            onClick={onClickDiscard}
          >
            Discard
          </Button>
        )}
        {showSimulateButton &&
          (simulateSupportedEvent && currentEnvironmentType === ApiModel.EnvironmentSandbox ? (
            <Button
              id='workflows-show__simulate_button'
              isPending={loading}
              onClick={createSimulatedWorkflowRun}
              variant='basic'
              disabled={isDirty || validationErrors.length > 0 || !hasStepsConfig}
            >
              Simulate
            </Button>
          ) : (
            <Tooltip
              placement='bottom'
              tooltip={
                <Paragraph style={{ maxWidth: '300px' }}>
                  {simulateSupportedEvent
                    ? 'Simulate runs in Sandbox environments. You can switch environments using the menu at the top left of the dashboard.'
                    : 'Simulate is not supported for this trigger event.'}
                </Paragraph>
              }
            >
              <Button
                id='workflows-show__simulate_button'
                isPending={loading}
                variant='basic'
                disabled={true}
              >
                Simulate
              </Button>
            </Tooltip>
          ))}
        {publishButton}
        <DropdownMenu
          trigger={
            <Button
              aria-label='More actions'
              disabled={loading}
              type='button'
              variant='inverse'
              width='icon'
            >
              <MoreIcon />
            </Button>
          }
        >
          {moreItems.map((item, i) =>
            item.routeTo ? (
              <DropdownMenu.Item
                as={Link}
                disabled={loading}
                id={item.id}
                key={i}
                to={item.routeTo}
              >
                {item.label}
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item
                disabled={loading}
                id={item.id}
                key={i}
                variant={item.variant ?? 'basic'}
                onClick={item.onClick}
              >
                {item.label}
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu>
      </HStack>
    </FullScreenHeaderContainer>
  );
};

export default WorkflowHeader;
